'use client'

import { useCallback, useMemo, useState } from 'react'

import { Animate } from '@syconium/little-miss-figgy'

import { reportClientError } from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { useCart } from '../../../app/_providers/CartProvider.client'
import { useFeatureFlag } from '../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { useOrganization } from '../../../app/_providers/OrganizationProvider.client'
import { useSession } from '../../../app/_providers/SessionProvider.client'
import { useSiteWideMessaging } from '../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { CtaButton } from '../../../components/atoms/CtaButton'
import { trackEvent } from '../../../lib/analytics'
import {
  formatPrettyCurrency,
  intlPrettyCurrency,
  isEmbroideryItem,
  isLogoEmbroiderySetup,
} from '../../../lib/utils'
import { KitGroup } from '../Cart'
import {
  CheckoutButtonWrapper,
  EmbroideryWarningContainer,
  EmptyCartMessage,
  MiniCartMessage,
  TotalSection,
  TotalSectionDivider,
} from '../Cart/styles'
import { groupKitItems, sortCartItems } from '../Cart/utils'
import { EmbroideryDisclosure } from '../EmbroideryDisclosure'
import { IntlShippingAndTaxMsg } from '../IntlShippingAndTaxMsg'

import { FreeShippingSection } from './FreeShipping'
import { MiniCartItem } from './MiniCartItem'
import { PriceLine } from './PriceLine'
import {
  CartItemsWrapper,
  CheckoutDisclosureText,
  CheckoutDisclosureWrapper,
  CheckoutErrorMessage,
  IntlMsgWrap,
  NewCustomerOffersText,
  PortalDiscountText,
  TotalAndCheckout,
  TotalLineItems,
} from './styles'
import { Upsells } from './Upsells'

/**
 * Temporary addition to support Product Bundle Discounts for the Ribbed Longsleeve Underscrub.
 * @deprecated do not use outside of temporary Product Bundle Discounts work.
 * @todo remove this after we remove this feature.
 */
type BundleSupportedProductGroups = {
  'womens-ribbed-longsleeve-underscrub': number
  'mens-technical-longsleeve-underscrub': number
  'womens-technical-longsleeve-underscrub': number
  'womens-salta-under-scrubs': number
  'mens-makato-under-scrubs': number
}
/** @deprecated */
const productGroupsThatSupportBundles = {
  'womens-ribbed-longsleeve-underscrub': 0,
  'mens-technical-longsleeve-underscrub': 0,
  'womens-technical-longsleeve-underscrub': 0,
  'womens-salta-under-scrubs': 0,
  'mens-makato-under-scrubs': 0,
} as BundleSupportedProductGroups
/** @deprecated */
const minBundleQuantityNeeded = 3

type MiniCartContentProps = {
  isRevealed: boolean
}

export const MiniCartContent = ({ isRevealed }: MiniCartContentProps) => {
  const {
    cart,
    status: cartStatus,
    errorMessage: cartErrorMessage,
    proceedToCheckout,
    removeItems,
    updateItemsQuantity,
  } = useCart()

  const { cart: miniCartCopy } = useTranslation()
  const { siteWidePrecheckoutMessage: miniCartMessage } = useSiteWideMessaging()
  const { locale, region, isUSRegion } = useLocalization()
  const { portal, stipend } = useOrganization()

  const [isCheckoutInFlight, setIsCheckoutInFlight] = useState<boolean>(false)

  const cartItems = Object.values(cart.items)

  const kitGroups = groupKitItems(cartItems)

  const formattedPrice = intlPrettyCurrency({
    value: cart.totalPrice,
    unit: 'cents',
    region: region,
    locale: locale,
    explicitFormat: true,
  })

  const handleCheckoutClick = useCallback(async () => {
    try {
      setIsCheckoutInFlight(true)
      await proceedToCheckout()
      setIsCheckoutInFlight(false)
    } catch (error) {
      setIsCheckoutInFlight(false)
      reportClientError({
        error,
        context: {
          scope: 'MiniCart',
        },
      })
    }
  }, [proceedToCheckout])

  const showEmbroideryDisclosure = Object.values(cart.items).some(item => isEmbroideryItem(item))

  const showCartLogoDigitizationText = Object.values(cart.items).some(item => {
    return isLogoEmbroiderySetup(item)
  })

  const showNewCustomerOffersMessage = useMemo(() => {
    const validSetProductTypes = ['Scrub Top', 'Scrub Pants']
    const validSets = kitGroups.filter(
      group =>
        // The SET consists of 2 items, a Scrub Top and Scrub Pants
        group.length === 2 && group.every(item => validSetProductTypes.includes(item.productType))
    )
    if (validSets.length > 0) {
      const anySetsHardMarked = validSets.some(set =>
        // When `_kitDiscountPct` is present, The SET is hard-marked.
        set.some(item => item.properties['_kitDiscountPct'])
      )
      return !anySetsHardMarked
    }
    return false
  }, [kitGroups])

  const hasPortalItemsSelected = useMemo(() => {
    return cartItems.some(item => item.properties['_isPortalColor'] === 'true')
  }, [cartItems])

  const showFreeReturnsDisclosure = useMemo(() => {
    // no free returns for `FINAL SALE` items (including embroidery)
    if (Object.values(cart.items).some(e => e.finalSale)) return false
    // respect the checks for the embroidery disclosure
    if (showEmbroideryDisclosure) return false
    return isUSRegion
  }, [cart.items, isUSRegion, showEmbroideryDisclosure])

  const { attributes } = useSession()
  const isFirstTimeVisitor = attributes.customerType === 'firstTime'

  const freeReturnsAndExchangesContent = useMemo(() => {
    if (isFirstTimeVisitor) {
      return miniCartCopy.returns.freeReturnsAndExchanges
    } else {
      return null
    }
  }, [isFirstTimeVisitor, miniCartCopy.returns.freeReturnsAndExchanges])

  /**
   * Start :: Temporary addition to support Product Bundle Discounts for the Ribbed Longsleeve Underscrub.
   * @deprecated do not use outside of temporary Product Bundle Discounts work.
   * @todo remove this after we remove this feature.
   */
  const productGroupHandlesThatSupportBundles = Object.keys(productGroupsThatSupportBundles)
  useMemo(() => {
    // Reset all quantities to 0
    Object.entries(productGroupsThatSupportBundles).forEach(
      ([key]) => (productGroupsThatSupportBundles[key as keyof BundleSupportedProductGroups] = 0)
    )
    // Rebuild quantity for each handle
    cartItems.forEach(cartItem => {
      if (
        cartItem.effectivePrice === cartItem.fullPrice &&
        productGroupHandlesThatSupportBundles.includes(cartItem.productGroupHandle)
      ) {
        productGroupsThatSupportBundles[
          cartItem.productGroupHandle as keyof BundleSupportedProductGroups
        ] += cartItem.quantity
      }
    })
  }, [cartItems, productGroupHandlesThatSupportBundles])

  const productBundleDiscountCopyFlag = useFeatureFlag<'hide-discount-copy' | 'show-discount-copy'>(
    {
      key: 'enable-product-bundle-discount-copy-text',
      controlVariant: 'hide-discount-copy',
      ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
      skip: !Object.values(productGroupsThatSupportBundles).some(
        value => value >= minBundleQuantityNeeded
      ),
    }
  )

  const showDiscountAtCheckoutCopy = useMemo(() => {
    return productBundleDiscountCopyFlag === 'show-discount-copy'
  }, [productBundleDiscountCopyFlag])

  // End :: Temporary addition to support Product Bundle Discounts for the Ribbed Longsleeve Underscrub.

  const hasStipend = portal && stipend !== null

  return (
    <>
      <FreeShippingSection cartTotal={cart.totalPrice} />
      {cart.itemCount > 0 ? (
        <CartItemsWrapper>
          {kitGroups.map(group => {
            const key = group.map(o => o.key).join('')
            return (
              <KitGroup
                key={key}
                cartItems={group}
                eventCategory='minicart'
                embroideryLabel={miniCartCopy.embroideryItemLabel}
                cartIsUpdating={cartStatus === 'pending'}
                decrementQuantity={() => {
                  updateItemsQuantity(
                    group.map(subItem => subItem.key),
                    -1
                  )
                }}
                incrementQuantity={() => {
                  updateItemsQuantity(
                    group.map(subItem => subItem.key),
                    1
                  )
                }}
                setQuantity={(newQuantity: number) => {
                  updateItemsQuantity(
                    group.map(subItem => subItem.key),
                    () => newQuantity
                  )
                }}
                removeAll={() => {
                  removeItems(group.map(subItem => subItem.key))
                }}
                removeItem={(key: string) => removeItems([key])}
              />
            )
          })}
          {sortCartItems(cartItems).map((item, index) => (
            <MiniCartItem
              key={`${item.key}-${index}`}
              item={item}
              index={index}
              showDiscountAtCheckoutCopy={
                showDiscountAtCheckoutCopy &&
                item.effectivePrice === item.fullPrice &&
                productGroupHandlesThatSupportBundles.includes(item.productGroupHandle) &&
                productGroupsThatSupportBundles[
                  item.productGroupHandle as keyof BundleSupportedProductGroups
                ] >= minBundleQuantityNeeded
              }
            />
          ))}
        </CartItemsWrapper>
      ) : (
        <Animate.FadeIn>
          <EmptyCartMessage>{miniCartCopy.emptyCartMessage}</EmptyCartMessage>
        </Animate.FadeIn>
      )}
      <TotalAndCheckout>
        {cart.itemCount > 0 ? <TotalSectionDivider /> : null}
        <TotalSection>
          <EmbroideryWarningContainer>
            {showEmbroideryDisclosure ? (
              <EmbroideryDisclosure
                style={{ marginBottom: '20px' }}
                showCartLogoDigitizationText={showCartLogoDigitizationText}
              />
            ) : null}
          </EmbroideryWarningContainer>
          {miniCartMessage && <MiniCartMessage>{miniCartMessage}</MiniCartMessage>}
          <TotalLineItems>
            {hasStipend && (
              <PriceLine
                type='stipend'
                label={miniCartCopy.stipendBalanceLabel}
                price={[
                  /*
                   * Stipend balance is always stored as USD, regardless of locale. We need to
                   * display the currency code alongside the symbol + price to make it clear
                   * that this value is always in USD.
                   */
                  formatPrettyCurrency({
                    value: stipend,
                    unit: 'dollars',
                    currency: 'USD',
                    locale: 'en-US',
                  }),
                  'USD',
                ].join(' ')}
                {...trackEvent({
                  category: 'minicart',
                  action: 'snapshot',
                  label: 'stipend balance',
                  value: stipend,
                })}
              />
            )}
            <PriceLine
              type='subtotal'
              label={miniCartCopy.subtotalLabel}
              price={formattedPrice}
              loading={cartStatus === 'pending'}
              {...trackEvent({
                category: 'minicart',
                action: 'snapshot',
                label: 'subtotal',
                value: cart.totalPrice,
              })}
            />

            {hasPortalItemsSelected && !!portal?.discountPercentage && (
              <PortalDiscountText>
                {portal?.name} {portal?.discountPercentage}
                {miniCartCopy.portalDiscountMessage}
              </PortalDiscountText>
            )}
          </TotalLineItems>
          {!isUSRegion ? (
            <IntlMsgWrap>
              <IntlShippingAndTaxMsg />
            </IntlMsgWrap>
          ) : null}
          {showNewCustomerOffersMessage ? (
            <NewCustomerOffersText $isIntlUser={!isUSRegion}>
              {miniCartCopy.newCustomerOffersAppliedAtCheckout}
            </NewCustomerOffersText>
          ) : null}
        </TotalSection>

        <CheckoutButtonWrapper>
          <CtaButton
            allCaps={true}
            block
            data-testid='checkout-button'
            borderless={cartItems.length === 0 || isCheckoutInFlight}
            disabled={cartItems.length === 0 || isCheckoutInFlight}
            isProcessing={isCheckoutInFlight}
            onClick={handleCheckoutClick}
            {...trackEvent({
              category: 'minicart',
              action: 'checkout',
              label: miniCartCopy.proceedToCheckoutCTA,
              value: cart.totalPrice,
            })}
          >
            {miniCartCopy.proceedToCheckoutCTA}
          </CtaButton>
          {cartErrorMessage ? (
            <CheckoutErrorMessage>{cartErrorMessage}</CheckoutErrorMessage>
          ) : null}
        </CheckoutButtonWrapper>
      </TotalAndCheckout>
      {showFreeReturnsDisclosure && (
        <CheckoutDisclosureWrapper>
          <CheckoutDisclosureText>{freeReturnsAndExchangesContent}</CheckoutDisclosureText>
        </CheckoutDisclosureWrapper>
      )}
      {isRevealed && <Upsells />}
    </>
  )
}
